import { ReactNode, Suspense } from "react";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { MediaCollection, ModelType, RelocationType } from "@/gql/graphql";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { useFileUpload } from "@/lib/FileUpload/Hooks/useFileUpload";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { ArrowUpRightIcon } from "lucide-react";
import { PDFIcon } from "@/assets/Icons";
import { cn } from "@/lib/utils";
import {
  EditableSettingRow,
  SettingRowsCard,
  DropzoneSettingRow,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { supplierTermsAndConditionsRecord } from "@/app/Suppliers/GraphQL/supplierTermsAndConditionsRecord";
import { updateSupplierTermsAndConditionsMutation } from "@/app/Suppliers/GraphQL/supplierMutations";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useTranslations } from "use-intl";
import { ResultOf } from "@graphql-typed-document-node/core";

type TermsRecord = NonNullable<
  ResultOf<
    typeof supplierTermsAndConditionsRecord
  >["supplierTermsAndConditions"]
>;

export function TermsAndConditionsSettingsCard({
  supplierId,
  relocationType = RelocationType.Relocation,
  title = "Rental Terms & Conditions",
  description = "Upload a PDF document of your terms and conditions. This will be shown to customers.",
  showLicenseRequirements = true,
  children,
}: {
  supplierId: string;
  relocationType?: RelocationType;
  title?: string;
  description?: string;
  showLicenseRequirements?: boolean;
  children?: ReactNode;
}) {
  return (
    <SettingRowsCard title={title} description={description}>
      <Suspense
        fallback={
          <div className="flex h-24 items-center justify-center">
            <Spinner className="size-5" />
          </div>
        }
      >
        <TermsRows
          supplierId={supplierId}
          relocationType={relocationType}
          showLicenseRequirements={showLicenseRequirements}
        />
      </Suspense>

      {children}
    </SettingRowsCard>
  );
}

function TermsRows({
  supplierId,
  relocationType,
  showLicenseRequirements,
}: {
  supplierId: string;
  relocationType: RelocationType;
  showLicenseRequirements: boolean;
}) {
  const { data, refetch } = useSuspenseGqlQuery(
    supplierTermsAndConditionsRecord,
    { supplierId, relocationType },
  );

  const terms = data.supplierTermsAndConditions;

  return (
    <>
      <TermsPdfRow
        supplierId={supplierId}
        relocationType={relocationType}
        terms={terms}
        refetch={refetch}
      />

      {showLicenseRequirements ? (
        <LicenseRequirementsRow
          supplierId={supplierId}
          relocationType={relocationType}
          terms={terms}
        />
      ) : null}
    </>
  );
}

function TermsPdfRow({
  supplierId,
  relocationType,
  terms,
  refetch,
}: {
  supplierId: string;
  relocationType: RelocationType;
  terms: TermsRecord | null | undefined;
  refetch: () => Promise<unknown>;
}) {
  const { mutateAsync: uploadFiles, isPending: isUploading } = useFileUpload();
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const { mutateAsync: updateTerms } = useGqlMutation(
    updateSupplierTermsAndConditionsMutation,
  );

  const handleFiles = async (files: File[]) => {
    try {
      let termsId = terms?.id;

      if (!termsId) {
        const res = await updateTerms({
          input: { supplier_id: supplierId, relocation_type: relocationType },
        });
        termsId = res.updateSupplierTermsAndConditions.id;
      }

      const [upload] = await uploadFiles(files.slice(0, 1));

      await createMedia({
        input: {
          file_name: upload.file.name,
          key: upload.filePath,
          collectionName: MediaCollection.TermsAndConditionsPdf,
          objectType: ModelType.SupplierTermsAndConditions,
          objectId: termsId,
        },
      });

      await refetch();
      toast.success("PDF uploaded successfully");
    } catch (err) {
      console.error(err);
      toast.warning("PDF upload failed");
    }
  };

  const dropzone = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    disabled: isUploading,
    onDropAccepted: handleFiles,
  });
  const pdf = terms?.pdf;

  return (
    <DropzoneSettingRow
      label="Document"
      dropzone={dropzone}
      actionLabel={pdf ? "Replace" : "Upload"}
    >
      <div
        className={cn(
          "relative flex h-10 w-64 items-center gap-2 rounded-md px-2.5 text-sm",
          pdf
            ? "border border-gray-200 bg-white"
            : "border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 group-hover:border-gray-400 group-hover:text-gray-600",
          dropzone.isDragActive && "border-indigo-500 bg-indigo-50",
        )}
      >
        <PDFIcon
          className={cn("size-5 shrink-0", pdf ? null : "opacity-40 grayscale")}
          aria-hidden
        />
        {pdf ? (
          <a
            href={pdf.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="truncate pr-4 font-medium text-gray-900 after:absolute after:inset-0 hover:text-indigo-600"
          >
            {pdf.file_name}
            <ArrowUpRightIcon
              className="absolute right-1.5 top-1.5 size-3.5 text-gray-400"
              aria-hidden
            />
          </a>
        ) : (
          <span>No PDF uploaded</span>
        )}
        {isUploading ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-white/70">
            <Spinner className="size-4" />
          </div>
        ) : null}
      </div>
    </DropzoneSettingRow>
  );
}

function LicenseRequirementsRow({
  supplierId,
  relocationType,
  terms,
}: {
  supplierId: string;
  relocationType: RelocationType;
  terms: TermsRecord | null | undefined;
}) {
  const t = useTranslations("settings.insurance.licenceRequirements");
  const { mutateAsync } = useGqlMutation(
    updateSupplierTermsAndConditionsMutation,
  );

  return (
    <EditableSettingRow
      label={t("label")}
      value={terms?.license_requirements}
      editClassName="max-w-2xl"
      initialValues={{
        license_requirements: terms?.license_requirements ?? "",
      }}
      onSave={async (values) => {
        await mutateAsync({
          input: {
            supplier_id: supplierId,
            relocation_type: relocationType,
            license_requirements: values.license_requirements || null,
          },
        });
      }}
    >
      <TextAreaInput
        name="license_requirements"
        label={t("label")}
        textAreaProps={{ rows: 4 }}
      />
    </EditableSettingRow>
  );
}
